import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PauseCircle, PlayCircle, Trash2, Save, ShieldAlert, X } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';
import AdminConfirm from '@/components/AdminConfirm';
import { FilterBar, SelectPill, Pagination, StateMsg } from '@/components/AdminFilters';

const STATUS_OPTS = [
  { value: 'all',       label: '모든 상태' },
  { value: 'active',    label: '정상' },
  { value: 'suspended', label: '정지' },
  { value: 'deleted',   label: '탈퇴' },
];
const ROLE_OPTS = [
  { value: 'all',         label: '모든 권한' },
  { value: 'user',        label: '일반회원' },
  { value: 'seller',      label: '판매자' },
  { value: 'admin',       label: '관리자' },
  { value: 'super_admin', label: '최고관리자' },
];
const JOINED_OPTS = [
  { value: 'all',   label: '전체 기간' },
  { value: 'today', label: '오늘' },
  { value: '7d',    label: '최근 7일' },
  { value: '30d',   label: '최근 30일' },
];

const roleBadge = (u) => {
  if (u.admin_role === 'super_admin' || u.role === 'super_admin') return { text: '최고관리자', cls: 'bg-purple-500/10 text-purple-600' };
  if (u.is_admin || u.admin_role === 'admin' || u.role === 'admin') return { text: '관리자', cls: 'bg-[#FF3B30]/10 text-[#FF3B30]' };
  if (u.seller_status === 'approved') return { text: '판매자', cls: 'bg-emerald-500/10 text-emerald-600' };
  return { text: '일반회원', cls: 'bg-gray-500/10 text-gray-600' };
};

const statusBadge = (u) => {
  if (u.is_deleted) return { text: '탈퇴', cls: 'bg-gray-500/10 text-gray-500' };
  if (u.is_suspended || u.seller_status === 'suspended') return { text: '정지', cls: 'bg-amber-500/10 text-amber-600' };
  return { text: '정상', cls: 'bg-emerald-500/10 text-emerald-600' };
};

const AdminUsers = ({ user: currentAdmin }) => {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [status, setStatus] = useState(params.get('status') || 'all');
  const [role, setRole] = useState(params.get('role') || 'all');
  const [joined, setJoined] = useState(params.get('joined') || 'all');
  const [page, setPage] = useState(Number(params.get('page') || 1));
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [memoDraft, setMemoDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/users', {
        params: {
          q: q || undefined,
          status: status !== 'all' ? status : undefined,
          role: role !== 'all' ? role : undefined,
          joined: joined !== 'all' ? joined : undefined,
          page, page_size: 20,
        },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '목록을 불러오지 못했습니다.');
    } finally { setLoading(false); }
  }, [q, status, role, joined, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const p = {};
    if (q) p.q = q;
    if (status !== 'all') p.status = status;
    if (role !== 'all') p.role = role;
    if (joined !== 'all') p.joined = joined;
    if (page > 1) p.page = String(page);
    setParams(p, { replace: true });
  }, [q, status, role, joined, page, setParams]);

  const reset = () => { setQ(''); setStatus('all'); setRole('all'); setJoined('all'); setPage(1); };

  const openDetail = async (u) => {
    try {
      const res = await api.get(`/admin/users/${u.user_id}`);
      setDetail(res.data);
      setMemoDraft(res.data.admin_memo || '');
    } catch (e) { toast.error('회원 정보를 불러오지 못했습니다.'); }
  };

  const doSuspend = (u) => setConfirm({
    title: '회원을 정지하시겠습니까?',
    message: `${u.name || u.email} 계정을 즉시 정지합니다.`,
    confirmLabel: '정지',
    onConfirm: async () => {
      try { const r = await api.post(`/admin/users/${u.user_id}/suspend`); toast.success(r.data.message); load(); setDetail(null); }
      catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); }
    },
  });

  const doUnsuspend = async (u) => {
    try { const r = await api.post(`/admin/users/${u.user_id}/unsuspend`); toast.success(r.data.message); load(); setDetail(null); }
    catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); }
  };

  const doDelete = (u) => setConfirm({
    title: '회원을 탈퇴 처리하시겠습니까?',
    message: `${u.name || u.email} 계정이 탈퇴 상태가 됩니다. 이 작업은 되돌리기 어려울 수 있습니다.`,
    confirmLabel: '탈퇴 처리',
    tone: 'danger',
    onConfirm: async () => {
      try { const r = await api.delete(`/admin/users/${u.user_id}`); toast.success(r.data.message); load(); setDetail(null); }
      catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); }
    },
  });

  const saveMemo = async () => {
    if (!detail) return;
    try {
      const r = await api.put(`/admin/users/${detail.user_id}/memo`, { admin_memo: memoDraft });
      toast.success(r.data.message);
      setDetail({ ...detail, admin_memo: memoDraft });
    } catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); }
  };

  const changeRole = async (newRole) => {
    if (!detail) return;
    try {
      const r = await api.put(`/admin/users/${detail.user_id}/role`, { admin_role: newRole });
      toast.success(r.data.message);
      setDetail({ ...detail, admin_role: newRole });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '권한 변경 실패'); }
  };

  const items = data.items || [];
  const isSuper = currentAdmin?.admin_role === 'super_admin' || currentAdmin?.role === 'super_admin';

  return (
    <AdminLayout user={currentAdmin} active="users">
      <div className="mb-4">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>회원 관리</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">회원 검색, 정지/해제, 탈퇴 처리, 권한 관리를 수행합니다.</p>
      </div>

      <FilterBar q={q} setQ={setQ} onSearch={() => { setPage(1); load(); }} onReset={reset} placeholder="이름 또는 이메일 검색">
        <SelectPill value={status} onChange={(v) => { setStatus(v); setPage(1); }} options={STATUS_OPTS} testid="filter-status" />
        <SelectPill value={role}   onChange={(v) => { setRole(v);   setPage(1); }} options={ROLE_OPTS}   testid="filter-role" />
        <SelectPill value={joined} onChange={(v) => { setJoined(v); setPage(1); }} options={JOINED_OPTS} testid="filter-joined" />
      </FilterBar>

      <div className="bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-xl overflow-hidden" data-testid="admin-users-table">
        <StateMsg loading={loading} empty={!loading && items.length === 0} emptyText="검색 결과가 없습니다." />
        {!loading && items.length > 0 && (
          <>
            {/* Desktop table */}
            <table className="hidden md:table w-full text-sm">
              <thead className="bg-black/5 dark:bg-white/5">
                <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-2">이름 / 이메일</th>
                  <th className="px-4 py-2">권한</th>
                  <th className="px-4 py-2">상태</th>
                  <th className="px-4 py-2">가입일</th>
                  <th className="px-4 py-2">마지막 로그인</th>
                  <th className="px-4 py-2 text-right">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5 dark:divide-white/10">
                {items.map((u) => {
                  const rb = roleBadge(u), sb = statusBadge(u);
                  return (
                    <tr key={u.user_id} className="hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer" onClick={() => openDetail(u)} data-testid={`user-row-${u.user_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-bold">{u.name || '-'}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${rb.cls}`}>{rb.text}</span></td>
                      <td className="px-4 py-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${sb.cls}`}>{sb.text}</span></td>
                      <td className="px-4 py-3 text-xs text-gray-500">{u.created_at ? format(new Date(u.created_at), 'yyyy-MM-dd', { locale: ko }) : '-'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{u.last_login_at ? format(new Date(u.last_login_at), 'yyyy-MM-dd HH:mm', { locale: ko }) : '-'}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        {(u.is_suspended || u.seller_status === 'suspended') ? (
                          <button onClick={() => doUnsuspend(u)} className="text-emerald-600 hover:underline text-xs font-bold" data-testid={`user-unsuspend-${u.user_id}`}>정지 해제</button>
                        ) : (
                          <button onClick={() => doSuspend(u)} className="text-amber-600 hover:underline text-xs font-bold" data-testid={`user-suspend-${u.user_id}`}>정지</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-black/5 dark:divide-white/10">
              {items.map((u) => {
                const rb = roleBadge(u), sb = statusBadge(u);
                return (
                  <button key={u.user_id} type="button" onClick={() => openDetail(u)}
                    className="w-full text-left px-4 py-3 flex flex-col gap-1 hover:bg-black/5 dark:hover:bg-white/5"
                    data-testid={`user-card-${u.user_id}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm truncate">{u.name || '-'}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${rb.cls}`}>{rb.text}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${sb.cls}`}>{sb.text}</span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">{u.email}</div>
                    <div className="text-[11px] text-gray-400">가입 {u.created_at?.slice(0, 10)} · 최근 로그인 {u.last_login_at?.slice(0, 16) || '-'}</div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      <Pagination page={page} total={data.total} pageSize={20} onChange={setPage} />

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setDetail(null)}>
          <div className="w-full max-w-lg bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-detail-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/5 dark:border-white/10">
              <h3 className="font-black text-lg">회원 상세</h3>
              <button onClick={() => setDetail(null)} className="p-1"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div><span className="text-gray-500">이름</span> · <strong>{detail.name}</strong></div>
              <div><span className="text-gray-500">이메일</span> · <strong>{detail.email}</strong></div>
              <div><span className="text-gray-500">권한</span> · <strong>{roleBadge(detail).text}</strong></div>
              <div><span className="text-gray-500">상태</span> · <strong>{statusBadge(detail).text}</strong></div>
              <div><span className="text-gray-500">가입일</span> · {detail.created_at ? format(new Date(detail.created_at), 'yyyy-MM-dd HH:mm', { locale: ko }) : '-'}</div>
              <div><span className="text-gray-500">마지막 로그인</span> · {detail.last_login_at ? format(new Date(detail.last_login_at), 'yyyy-MM-dd HH:mm', { locale: ko }) : '-'}</div>

              <div className="pt-3 border-t border-dashed border-black/10 dark:border-white/10">
                <label className="text-xs font-bold text-gray-500 mb-1 block">관리자 내부 메모</label>
                <textarea value={memoDraft} onChange={(e) => setMemoDraft(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-[#141414] border-black/10 dark:border-white/10 text-sm"
                  rows={3} data-testid="user-memo-input" />
                <button onClick={saveMemo} className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-[#FF3B30] text-white hover:bg-[#D32F2F]" data-testid="user-memo-save">
                  <Save size={12} /> 메모 저장
                </button>
              </div>

              {isSuper && (
                <div className="pt-3 border-t border-dashed border-black/10 dark:border-white/10">
                  <label className="text-xs font-bold text-gray-500 mb-1 block">권한 변경 <span className="text-[10px] text-gray-400">(super_admin 전용)</span></label>
                  <div className="flex flex-wrap gap-2">
                    {['none', 'moderator', 'admin', 'super_admin'].map((r) => (
                      <button key={r} onClick={() => changeRole(r)}
                        className={`px-3 py-1 rounded-full text-xs font-bold border ${detail.admin_role === r ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-black/10 dark:border-white/10'}`}
                        data-testid={`user-role-${r}`}>{r}</button>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-dashed border-black/10 dark:border-white/10 flex flex-wrap gap-2">
                {(detail.is_suspended || detail.seller_status === 'suspended') ? (
                  <button onClick={() => doUnsuspend(detail)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-600 text-white"><PlayCircle size={12} /> 정지 해제</button>
                ) : (
                  <button onClick={() => doSuspend(detail)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-500 text-white"><PauseCircle size={12} /> 정지</button>
                )}
                <button onClick={() => doDelete(detail)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-red-600 text-white ml-auto"><Trash2 size={12} /> 탈퇴 처리</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminConfirm state={confirm} onClose={() => setConfirm(null)} />
    </AdminLayout>
  );
};

export default AdminUsers;
